// CAMPO DE ESCOLHA COM DIGITAÇÃO — o substituto do <select> nos heros.
//
// Por que existe: o dropdown do <select> é desenhado pelo sistema operacional e não aceita CSS,
// então numa linha de busca onde um campo já é combobox (lista com painel próprio) os outros
// destoam — abrem a listinha crua do browser. Este componente embrulha o SmartLocationSearch
// para que QUALQUER lista de opções [{label, value}] vire o mesmo campo: clica e vê a lista
// inteira, digita e filtra.
//
// O valor é DERIVADO do texto (fonte única): escolher na lista preenche o texto e o efeito
// resolve o `value` que o hero já esperava do <select> — nenhum outro estado muda de forma.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { normalizaTermo } from '../../../utils/buscaLocal';
import type { Sugestao } from '../../../utils/buscaLocal';
import SmartLocationSearch from './SmartLocationSearch';

export interface OpcaoCampo {
    label: string;
    value: any;
}

export interface CampoSelecaoProps {
    opcoes: OpcaoCampo[];
    /** valor selecionado hoje ('' = nenhum). */
    valor: string;
    onChange: (valor: string) => void;
    /**
     * Rótulo da opção que limpa a escolha ("Todas as cidades") — o `<option value="">` do
     * select. Sem ele o campo é obrigatório (ex.: Transação): não aceita ficar vazio e o texto
     * volta para a escolha atual quando a pessoa sai sem escolher nada.
     */
    rotuloVazio?: string;
    ariaLabel: string;
    className?: string;
    /** teto de itens listados (o padrão do filtro é curto demais para listas de tipo/cidade). */
    limite?: number;
}

const CampoSelecao: React.FC<CampoSelecaoProps> = ({
    opcoes,
    valor,
    onChange,
    rotuloVazio,
    ariaLabel,
    className,
    limite = 60,
}) => {
    const obrigatorio = !rotuloVazio;

    const indice: Sugestao[] = useMemo(
        () =>
            (opcoes || [])
                .filter((o) => o?.label)
                .map((o, i) => ({
                    categoria: 'cidade' as const, // só define o título do grupo, que fica oculto
                    id: 0,
                    label: String(o.label),
                    busca: normalizaTermo(o.label),
                    // A lista chega pronta de quem chamou (as cidades já vêm com as priorizadas
                    // do tenant no topo, via formatSelects) — a ordem dela é para ser mantida.
                    ordem: i,
                })),
        [opcoes]
    );

    const rotuloDoValor = useMemo(
        () => opcoes?.find((o) => String(o.value) === String(valor))?.label || '',
        [opcoes, valor]
    );

    const [texto, setTexto] = useState(rotuloDoValor);
    // Guarda o último valor que ESTE campo aplicou, para distinguir de uma mudança vinda de fora.
    const aplicadoRef = useRef(valor);
    const rotuloRef = useRef(rotuloDoValor);
    rotuloRef.current = rotuloDoValor;

    // Valor trocado por fora (ex.: a transação padrão que só existe depois do primeiro render):
    // o texto acompanha.
    useEffect(() => {
        if (String(aplicadoRef.current) !== String(valor)) {
            aplicadoRef.current = valor;
            setTexto(rotuloDoValor);
        }
    }, [valor, rotuloDoValor]);

    useEffect(() => {
        const aplicar = (novo: string) => {
            aplicadoRef.current = novo;
            onChange(novo);
        };
        const termo = normalizaTermo(texto);

        if (!termo) {
            // Campo obrigatório não volta a "vazio" enquanto a pessoa apaga para redigitar:
            // mantém a escolha em vigor até existir outra.
            if (!obrigatorio) aplicar('');
            return;
        }

        const exatas = opcoes.filter((o) => normalizaTermo(o.label) === termo);
        const candidatas = exatas.length
            ? exatas
            : opcoes.filter((o) => normalizaTermo(o.label).startsWith(termo));
        // Só resolve quando o texto aponta para UMA opção; ambíguo não escolhe por conta própria.
        if (candidatas.length === 1) aplicar(String(candidatas[0].value));
        else if (!obrigatorio) aplicar('');
        // `onChange` fora das deps de propósito: é recriado a cada render pelo hero e reexecutaria
        // o efeito à toa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [texto, opcoes, obrigatorio]);

    return (
        <SmartLocationSearch
            indice={indice}
            estado="pronto"
            onAtivar={() => undefined}
            valor={texto}
            onChangeValor={setTexto}
            // A escolha vira texto e o efeito acima resolve o valor — nada a fazer aqui.
            onEscolher={() => undefined}
            onLimparEscolha={() => undefined}
            // Texto solto que não virou escolha não pode ficar na tela prometendo um filtro que
            // não existe: volta para o que está valendo.
            onSair={() => setTexto(rotuloRef.current)}
            placeholder={rotuloVazio || rotuloDoValor}
            ariaLabel={ariaLabel}
            minimoParaAbrir={0}
            limitePorGrupo={limite}
            comSeta
            semCodigo
            opcaoVazia={rotuloVazio}
            className={className}
        />
    );
};

export default CampoSelecao;
